"use client";

import { useEffect, useId, useMemo, useRef, useState } from "react";

import { IconaGiu, controlloFaccetta } from "@/components/search/Faccette";
import { FUOCO } from "@/components/ui/pulsanti";

type Option = {
  value: string;
  label: string;
  /** Tinta della voce, resa come pallino davanti all'etichetta. La usa
   *  il menu dei tipi per non perdere i toni editoriali che i vecchi
   *  chip avevano (PRIORITY_COLORS): sono nomi di variabile, quindi
   *  seguono il tema chiaro e quello scuro. */
  tone?: string;
};

type OpzioniComuni = {
  /** Glifo/icona a sinistra dell'etichetta — fa capire al colpo
   *  d'occhio di che filtro si tratta (◉ Paesi, ◔ Periodo, ecc.). */
  icon?: string;
  /** Label di default quando nessuna opzione è selezionata
   *  (es. "Tutti i Paesi"). Anche l'opzione "vuota" della lista. */
  placeholder: string;
  options: Option[];
  /** Mostra un input "Cerca…" sopra alla lista, filtra le opzioni
   *  per substring case-insensitive. Utile per liste molto lunghe
   *  (200+ Paesi). */
  searchable?: boolean;
  /** Testo guida del campo di ricerca interno al panel. */
  searchPlaceholder?: string;
  /** Larghezza minima del panel dropdown (default 220px). */
  panelMinWidth?: number;
  /** Etichetta accessibile per il bottone. */
  ariaLabel?: string;
  /** Classi extra sulla radice — serve al form di ricerca per farne
   *  una mezza colonna a tutta larghezza sotto sm. */
  className?: string;
  /** La pelle del bottone. `barra` è il segmento maiuscolo mono dentro
   *  una cornice (la barra di prima); `faccetta` è il controllo dei
   *  filtri del Rullo (components/search/Faccette.tsx,
   *  `controlloFaccetta`): sans, a tutta larghezza, con l'etichetta
   *  scritta sopra da chi lo usa. Dal 30/09/2026 le barre di ricerca
   *  del sito usano tutte `faccetta`. */
  variante?: "barra" | "faccetta";
};

/** Il caso di sempre: una voce per volta, il panel si chiude appena si
 *  sceglie. */
type Singola = OpzioniComuni & {
  multiple?: false;
  value: string;
  onChange: (value: string) => void;
};

/** Selezione multipla: le voci si accumulano e il panel resta aperto,
 *  perché sceglierne tre vorrebbe dire riaprirlo tre volte. Il bottone
 *  mostra la prima scelta più quante altre ce ne sono, così la barra
 *  dice sempre che cosa sta filtrando anche da chiusa. */
type Multipla = OpzioniComuni & {
  multiple: true;
  value: string[];
  onChange: (value: string[]) => void;
};

type FilterSelectProps = Singola | Multipla;

/** Mini combobox custom usato dentro al form di ricerca al posto dei
 *  `<select>` nativi. Stessa estetica editoriale del form (border-r
 *  per separare, font mono uppercase), ma con:
 *   • icona + caret ▾ per segnalare che è cliccabile,
 *   • panel dropdown con input "cerca" opzionale per filtrare i Paesi,
 *   • close su click-outside e su Escape,
 *   • focus automatico sull'input quando il panel apre,
 *   • selezione multipla (`multiple`), dove le scelte si accumulano e
 *     il panel resta aperto: la usa il menu dei tipi, che ha preso il
 *     posto della striscia di chip.
 */
export function FilterSelect(props: FilterSelectProps) {
  const {
    icon,
    placeholder,
    options,
    searchable = false,
    searchPlaceholder = "Cerca…",
    panelMinWidth = 220,
    ariaLabel,
    className = "",
    variante = "barra",
  } = props;
  const multiple = props.multiple === true;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();

  // Chiusura del panel su click-outside / Escape. Listener solo
  // quando il panel è aperto, per non sporcare il document a vuoto.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Focus automatico sul campo Cerca all'apertura, così non serve
  // un secondo click per cominciare a digitare.
  useEffect(() => {
    if (open && searchable) {
      const t = setTimeout(() => inputRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
  }, [open, searchable]);

  const filtered = useMemo(() => {
    if (!searchable || !query.trim()) return options;
    const q = query.trim().toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query, searchable]);

  /** Le scelte, sempre come lista: il caso singolo è la lista di uno.
   *  Tenute nell'ordine delle `options` e non in quello dei clic, così
   *  l'etichetta del bottone non si riordina sotto le dita. */
  const scelte = useMemo(() => {
    const attive = props.multiple
      ? props.value
      : props.value
        ? [props.value]
        : [];
    return options.filter((o) => attive.includes(o.value));
  }, [options, props.multiple, props.value]);

  const valori = scelte.map((o) => o.value);
  const isActive = scelte.length > 0;
  const display =
    scelte.length === 0
      ? placeholder
      : scelte.length === 1
        ? scelte[0]!.label
        : `${scelte[0]!.label} +${scelte.length - 1}`;

  /** Un clic su una voce. Nel caso singolo sostituisce e chiude, nel
   *  multiplo aggiunge o toglie e lascia il panel dov'è. */
  function scegli(v: string) {
    if (props.multiple) {
      const next = valori.includes(v)
        ? valori.filter((x) => x !== v)
        : options.filter((o) => o.value === v || valori.includes(o.value)).map((o) => o.value);
      props.onChange(next);
      return;
    }
    props.onChange(v);
    setOpen(false);
    setQuery("");
  }

  /** La voce "vuota" in cima: azzera il filtro in tutti e due i casi. */
  function azzera() {
    if (props.multiple) props.onChange([]);
    else props.onChange("");
    setOpen(false);
    setQuery("");
  }

  return (
    <div className={`relative flex min-w-0 items-stretch ${className}`} ref={rootRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-label={ariaLabel}
        /* `px-3.5` e `text-nota`, come ogni altro segmento della cornice
           (la lente, la targhetta del contesto, il conto): erano cinque
           rientri diversi — 10, 12, 14, 20 — e due corpi, e la riga
           usciva a passo irregolare. */
        className={
          variante === "faccetta"
            ? `${controlloFaccetta(isActive)} flex items-center justify-between gap-2 py-2 pr-3 pl-3 text-left ${FUOCO}`
            : `flex min-w-0 flex-1 cursor-pointer items-center justify-center gap-2 border-r border-rule px-3.5 text-nota font-semibold uppercase tracking-etichetta transition-colors sm:justify-start ${
                isActive
                  ? "bg-accent text-onAccent"
                  : "bg-accentSoft text-onAccentSoft hover:bg-accent hover:text-onAccent"
              }`
        }
      >
        {variante === "faccetta" ? (
          <>
            <span className="min-w-0 truncate">{display}</span>
            <IconaGiu aperto={open} />
          </>
        ) : (
          <>
            {icon ? (
              <span aria-hidden className="text-[13px]">
                {icon}
              </span>
            ) : null}
            <span className="min-w-0 truncate sm:max-w-[160px]">
              {display.toUpperCase()}
            </span>
            <span
              aria-hidden
              className={`shrink-0 text-[11px] leading-none transition-transform ${
                open ? "rotate-180" : ""
              }`}
            >
              ▾
            </span>
          </>
        )}
      </button>

      {open ? (
        <div
          // `max-width` legato al viewport: su telefono un panel da
          // 240px ancorato a un bottone già vicino al bordo destro
          // sfoderebbe fuori schermo e verrebbe tagliato dal
          // `overflow-x: clip` del body.
          className="absolute left-0 top-[calc(100%+4px)] z-50 max-w-[calc(100vw-2rem)] rounded-sm border border-rule bg-surface shadow-[0_10px_32px_rgba(0,0,0,0.18)]"
          style={{ minWidth: `min(${panelMinWidth}px, calc(100vw - 2rem))` }}
          id={listId}
          role="listbox"
          aria-multiselectable={multiple || undefined}
        >
          {searchable ? (
            <div className="border-b border-rule p-2">
              <div className="flex items-stretch rounded-sm border border-rule bg-bg focus-within:border-accent">
                <span
                  aria-hidden
                  className="flex items-center px-2 text-[14px] text-accent"
                >
                  ⌕
                </span>
                <input
                  ref={inputRef}
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={searchPlaceholder}
                  className="flex-1 bg-transparent py-1.5 pr-2 font-sans text-voce text-ink outline-none placeholder:text-muted"
                />
                {query ? (
                  <button
                    type="button"
                    onClick={() => setQuery("")}
                    aria-label="Azzera ricerca"
                    className="cursor-pointer px-2 text-[12px] text-muted hover:text-accent"
                  >
                    ✕
                  </button>
                ) : null}
              </div>
            </div>
          ) : null}

          <ul className="oi-scroll max-h-[300px] overflow-y-auto py-1">
            {/* Opzione "vuota" sempre in cima — equivale al "Tutti i …"
                e azzera il filtro. */}
            <li role="option" aria-selected={!isActive}>
              <button
                type="button"
                onClick={azzera}
                className={`flex w-full cursor-pointer items-center justify-between gap-3 px-3 py-2 text-left font-sans text-voce transition-colors hover:bg-accentSoft ${
                  !isActive
                    ? "bg-accentSoft font-semibold text-onAccentSoft"
                    : "text-ink"
                }`}
              >
                <span className="truncate">{placeholder}</span>
                {!isActive ? (
                  <span aria-hidden className="text-accent">
                    ✓
                  </span>
                ) : null}
              </button>
            </li>

            {filtered.length === 0 ? (
              <li className="px-3 py-2 font-sans text-voce text-muted">
                Nessun risultato
              </li>
            ) : (
              filtered.map((o) => {
                const selected = valori.includes(o.value);
                return (
                  <li key={o.value} role="option" aria-selected={selected}>
                    <button
                      type="button"
                      onClick={() => scegli(o.value)}
                      className={`flex w-full cursor-pointer items-center justify-between gap-3 px-3 py-2 text-left font-sans text-voce transition-colors hover:bg-accentSoft ${
                        selected
                          ? "bg-accentSoft font-semibold text-onAccentSoft"
                          : "text-ink"
                      }`}
                    >
                      <span className="flex min-w-0 items-center gap-2">
                        {o.tone ? (
                          <span
                            aria-hidden
                            className="inline-block h-1.5 w-1.5 flex-shrink-0 rounded-full"
                            style={{ backgroundColor: o.tone }}
                          />
                        ) : null}
                        <span className="truncate">{o.label}</span>
                      </span>
                      {selected ? (
                        <span aria-hidden className="text-accent">
                          ✓
                        </span>
                      ) : null}
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
